<template>
  <div>
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="3"
            xl="2"
          >
            <b-form-group
              label="Ano"
              label-for="filter-year"
            >
              <v-select
                v-model="filterYear"
                :options="yearOptions"
                :reduce="val => val.value"
                :clearable="false"
                input-id="filter-year"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
            xl="2"
          >
            <b-form-group
              label="Mês"
              label-for="filter-month"
            >
              <v-select
                v-model="filterMonth"
                :options="monthOptions"
                :reduce="val => val.value"
                :clearable="false"
                input-id="filter-month"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="5"
            xl="4"
          >
            <label for="institution-id">Instituição</label>
            <v-select
              id="institution-id"
              v-model="institutionId"
              name="institution-id"
              :options="institutionOptions"
              label="label"
              item-value="value"
              item-text="label"
            >
              <span slot="no-options">Nenhuma instituição encontrada.</span>
            </v-select>
          </b-col>
          <b-col
            cols="12"
            md="8"
            xl="4"
          >
            <label class="invisible d-none d-md-block">Ações</label>
            <div class="d-flex justify-content-end">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="btn-icon mr-1"
                @click="loadReport"
              >
                Buscar
                <feather-icon
                  icon="SearchIcon"
                  size="16"
                />
              </b-button>
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="outline-primary"
                class="btn-icon"
                :disabled="!generalData"
                @click="downloadReport"
              >
                Exportar
                <feather-icon
                  icon="DownloadIcon"
                  size="16"
                />
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <b-card
      v-if="generalData"
      class="mt-2"
    >
      <b-card-title>
        Geral
      </b-card-title>
      <b-row>
        <b-col
          v-for="stat in generalStats"
          :key="stat.label"
          cols="6"
          md="4"
          lg="3"
          xl="2"
          class="mb-2"
        >
          <div class="text-muted">
            {{ stat.label }}
          </div>
          <div class="font-weight-bold">
            {{ stat.value }}
          </div>
        </b-col>
      </b-row>
    </b-card>

    <b-card
      v-if="generalData"
      no-body
      class="mt-2 mb-0"
    >
      <b-tabs
        class="g-med-tabs w-100"
        pills
        fill
        justified
      >
        <b-tab active>
          <template #title>
            <div>
              <feather-icon
                icon="MapPinIcon"
                size="16"
                class="mr-0 mr-sm-50"
              />
              <span class="d-none d-sm-inline">Instituição</span>
            </div>
          </template>
          <b-table
            class="position-relative"
            :items="byInstitution"
            responsive
            sticky-header="900px"
            :fields="institutionColumns"
            primary-key="institution_id"
            show-empty
            empty-text="Nenhum registro encontrado"
          >
            <template #cell(institution_name)="{ item }">
              <div :title="item.institution_name">
                <div>{{ truncateString(item.institution_name, 40) }}</div>
                <div class="font-small-2">
                  {{ formatCnpj(item.institution_cnpj) }}
                </div>
              </div>
            </template>
            <template #head(physicians_on_duty)>
              <b-tooltip target="inst-med-plantoes">
                Profissionais Lançados
              </b-tooltip>
              <span id="inst-med-plantoes">Prof. Lançado</span>
            </template>
            <template #head(physicians_anticipated)>
              <b-tooltip target="inst-med-antecipou">
                Profissionais que Antecipou
              </b-tooltip>
              <span id="inst-med-antecipou">Prof. Antecipou</span>
            </template>
            <template #head(percentage_anticipated_over_launched)>
              <b-tooltip target="inst-a-l">
                Antecipado vs Lançado
              </b-tooltip>
              <span id="inst-a-l">A vs L</span>
            </template>
            <template #head(percentage_released_over_launched)>
              <b-tooltip target="inst-lb-l">
                Liberado vs Lançado
              </b-tooltip>
              <span id="inst-lb-l">Lb vs L</span>
            </template>
            <template #head(percentage_anticipated_over_released)>
              <b-tooltip target="inst-a-lb">
                Antecipado vs Liberado
              </b-tooltip>
              <span id="inst-a-lb">A vs Lb</span>
            </template>
            <template #head(percentage_anticipated_over_limit)>
              <b-tooltip target="inst-a-li">
                Antecipado vs Limite
              </b-tooltip>
              <span id="inst-a-li">A vs Li</span>
            </template>
          </b-table>
        </b-tab>
        <b-tab>
          <template #title>
            <div>
              <feather-icon
                icon="HomeIcon"
                size="16"
                class="mr-0 mr-sm-50"
              />
              <span class="d-none d-sm-inline">Unidade</span>
            </div>
          </template>
          <b-table
            class="position-relative"
            :items="byWorkplace"
            responsive
            sticky-header="900px"
            :fields="workplaceColumns"
            primary-key="workplace_id"
            show-empty
            empty-text="Nenhum registro encontrado"
          >
            <template #cell(workplace_name)="{ item }">
              <div :title="item.workplace_name">
                <div>{{ truncateString(item.workplace_name, 40) }}</div>
                <div class="font-small-2">
                  {{ truncateString(item.institution_name, 40) }}
                </div>
              </div>
            </template>
            <template #head(physicians_on_duty)>
              <b-tooltip target="wp-med-plantoes">
                Profissionais Lançados
              </b-tooltip>
              <span id="wp-med-plantoes">Prof. Lançado</span>
            </template>
            <template #head(physicians_anticipated)>
              <b-tooltip target="wp-med-antecipou">
                Profissionais que Antecipou
              </b-tooltip>
              <span id="wp-med-antecipou">Prof. Antecipou</span>
            </template>
            <template #head(percentage_anticipated_over_launched)>
              <b-tooltip target="wp-a-l">
                Antecipado vs Lançado
              </b-tooltip>
              <span id="wp-a-l">A vs L</span>
            </template>
            <template #head(percentage_released_over_launched)>
              <b-tooltip target="wp-lb-l">
                Liberado vs Lançado
              </b-tooltip>
              <span id="wp-lb-l">Lb vs L</span>
            </template>
            <template #head(percentage_anticipated_over_released)>
              <b-tooltip target="wp-a-lb">
                Antecipado vs Liberado
              </b-tooltip>
              <span id="wp-a-lb">A vs Lb</span>
            </template>
          </b-table>
        </b-tab>
      </b-tabs>
    </b-card>

    <b-card
      v-if="!generalData"
      class="mt-2"
    >
      <h4 class="text-center w-100 my-3">
        Não há registros
      </h4>
    </b-card>
  </div>
</template>

<script>
import store from '@/store'
import Ripple from 'vue-ripple-directive'
import { onUnmounted } from '@vue/composition-api'
import vSelect from 'vue-select'
import {
  BCard, BCardTitle, BRow, BCol, BFormGroup, BButton, BTabs, BTab, BTable, BTooltip,
} from 'bootstrap-vue'
import reportStoreModule from '../reportStoreModule'
import useMonthlyLaunchIndicator from './useMonthlyLaunchIndicator'

export default {
  directives: {
    Ripple,
  },
  components: {
    BCard,
    BCardTitle,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BTabs,
    BTab,
    BTable,
    BTooltip,
    vSelect,
  },
  setup() {
    const REPORTS_STORE_MODULE_NAME = 'app-reports'
    if (!store.hasModule(REPORTS_STORE_MODULE_NAME)) store.registerModule(REPORTS_STORE_MODULE_NAME, reportStoreModule)

    onUnmounted(() => {
      if (store.hasModule(REPORTS_STORE_MODULE_NAME)) store.unregisterModule(REPORTS_STORE_MODULE_NAME)
    })

    const {
      loading,
      filterYear,
      filterMonth,
      yearOptions,
      monthOptions,
      institutionId,
      institutionOptions,
      fetchInstitutions,
      generalData,
      generalStats,
      byInstitution,
      byWorkplace,
      institutionColumns,
      workplaceColumns,
      loadReport,
      downloadReport,
      truncateString,
      formatCnpj,
    } = useMonthlyLaunchIndicator()

    fetchInstitutions()

    return {
      loading,
      filterYear,
      filterMonth,
      yearOptions,
      monthOptions,
      institutionId,
      institutionOptions,
      generalData,
      generalStats,
      byInstitution,
      byWorkplace,
      institutionColumns,
      workplaceColumns,
      loadReport,
      downloadReport,
      truncateString,
      formatCnpj,
    }
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

label {
  margin-top: 0.2857rem!important;
}
</style>
